import { CheckmarkIcon, CopyIcon } from "components/Icons";
import { PropsWithChildren } from "react";
import * as Sentry from "@sentry/browser";
import { Spacer } from "components/general/Spacer/Spacer";
import { Button, ColorType } from "../general/Button/Button";
import { SerializedStyles } from "@emotion/react";

interface CopyButtonProps {
  isCopied: boolean;
  setIsCopied: React.Dispatch<React.SetStateAction<boolean>>;
  copyValue: string;
  iconPlacement?: "left" | "right";
  buttonType?: ColorType;
  buttonCss?: SerializedStyles;
  iconSize?: number | string;
  iconGap?: number | string;
}

export const CopyButton = ({
  isCopied,
  setIsCopied,
  buttonType,
  copyValue,
  iconPlacement,
  iconSize = "100%",
  iconGap = 4,
  children,
  buttonCss,
}: PropsWithChildren<CopyButtonProps>) => {
  const isCopiedIcon = isCopied ? (
    <CheckmarkIcon width={iconSize} height={iconSize} />
  ) : (
    <CopyIcon width={iconSize} height={iconSize} />
  );
  const content = () => {
    if (iconPlacement === "left") {
      return (
        <>
          {isCopiedIcon}
          <Spacer width={iconGap} />
          {children}
        </>
      );
    } else if (iconPlacement === "right") {
      return (
        <>
          {children}
          <Spacer width={iconGap} />
          {isCopiedIcon}
        </>
      );
    }
    return isCopiedIcon;
  };

  return (
    <Button
      buttonType={buttonType}
      buttonCss={buttonCss}
      onClick={() => handleCopy(copyValue, setIsCopied)}
    >
      {content()}
    </Button>
  );
};

// Helpers

export const handleCopy = (
  value: string,
  setCopied: (value: React.SetStateAction<boolean>) => void
) => {
  if (value) {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      })
      .catch(err => {
        console.log(err);
        Sentry.captureException(`Could not copy value to clipboard`, {
          extra: { value },
        });
      });
  }
};
